import type { FC, PropsWithChildren, ReactNode } from "react";

import { Price } from "@ui/components/Price";
import { StrengthIndicator } from "@ui/components/StrengthIndicator";
import { useTranslations } from "@ui/providers/useTranslations";
import { getOptimizedMagentoImageSrcSet } from "@ui/utils/getOptimizedMagentoImageSrcSet";
import classNames from "classnames";

import { ImageBehaviour } from "@jti/core-components/components/ImageBehaviour";
import { getCSSCustomProperty } from "@jti/fe-common";
import type { ProductNicotineStrength } from "@jti/magento";

import { OutOfStock } from "../OutOfStock";

import styles from "./ProductMiniature.module.scss";

const COMPACT_IMAGE_SIZE_TOKEN = "--dds-size-1500";
const COMPACT_IMAGE_SIZE_FALLBACK_PX = 60;

export type ProductMiniatureProps = {
  imageUrl: string;
  name: string;
  price?: {
    currency: string;
    value: number;
  };
  url?: string;
  imageClassName?: string;
  showTitle?: boolean;
  subtitle?: string;
  outOfStock?: boolean;
  variant?: "compact" | "variantBig";
  isSubscription?: boolean;
  badge?: ReactNode;
  nicotineStrength?: ProductNicotineStrength;
};

function LinkWrapper(props: { url: string | undefined; children?: React.ReactNode; className: string }) {
  return (
    <>
      {props.url ? (
        <a href={props.url} className={props.className}>
          {props.children}
        </a>
      ) : (
        props.children
      )}
    </>
  );
}

export const ProductMiniature: FC<PropsWithChildren<ProductMiniatureProps>> = ({
  children,
  imageUrl,
  name,
  price,
  url,
  imageClassName = "",
  showTitle = true,
  subtitle,
  outOfStock,
  variant,
  isSubscription = false,
  badge,
  nicotineStrength,
}) => {
  const { __ } = useTranslations();

  if (variant === "compact") {
    const compactImageSizePx =
      getCSSCustomProperty(COMPACT_IMAGE_SIZE_TOKEN, COMPACT_IMAGE_SIZE_FALLBACK_PX) ?? COMPACT_IMAGE_SIZE_FALLBACK_PX;
    const { optimizedImageSrcSet, optimizedImageSrc1x } = getOptimizedMagentoImageSrcSet({
      src: imageUrl,
      baseWidth: compactImageSizePx,
      baseHeight: compactImageSizePx,
    });

    return (
      <LinkWrapper url={url} className={styles.compactLinkWrapper}>
        <div className={styles.compactContainer}>
          <div className={styles.compactImageWrapper}>
            <ImageBehaviour
              src={optimizedImageSrc1x ?? imageUrl}
              srcSet={optimizedImageSrcSet}
              width={compactImageSizePx}
              height={compactImageSizePx}
              alt={name}
              className={classNames(styles.compactImage, imageClassName)}
              nativeBehaviour
            />
          </div>
          {showTitle && (
            <div className={styles.compactTitleAndPriceContainer}>
              <div className={styles.compactTitle}>{name}</div>
              {nicotineStrength && (
                <StrengthIndicator name={nicotineStrength.name} strength={nicotineStrength.rating} />
              )}
              {price && (
                <div>
                  <Price
                    price={price}
                    className={styles.compactPrice}
                    frequencyClassName={styles.compactFrequency}
                    frequency={isSubscription ? __("cart.cart_item.subscription_billing_frequency") : undefined}
                  />
                </div>
              )}
              {outOfStock && <OutOfStock hideIcon={true} />}
              {children ? <div>{children}</div> : null}
            </div>
          )}
        </div>
      </LinkWrapper>
    );
  }
  return (
    <LinkWrapper url={url} className={styles.linkWrapper}>
      <div className={classNames(styles.container, variant ? styles[variant] : null)}>
        <div
          style={{ backgroundImage: `url(${imageUrl})` }}
          role="img"
          aria-label={name}
          className={classNames(styles.image, imageClassName)}
        ></div>

        {showTitle && (
          <div className={styles.name}>
            {badge && <div className={styles.badge}>{badge}</div>}
            <strong className={styles.productName}>{name}</strong>
            {outOfStock && <OutOfStock hideIcon={true} />}
            {subtitle && (
              <div className={styles.subtitle} data-testid="subtitle">
                {subtitle}
              </div>
            )}
            {nicotineStrength && <StrengthIndicator name={nicotineStrength.name} strength={nicotineStrength.rating} />}
            {children ? <div className={styles.children}>{children}</div> : null}
          </div>
        )}
      </div>
    </LinkWrapper>
  );
};
